import React from 'react';
import { StringParam, UrlUpdateType, useQueryParams } from 'use-query-params';

import { ResultsExportType } from './exportTypes';
import { encodePanelRunSelections, parsePanelRunSelections } from './workflowResultsSelection';

const resultsExportQueryParams = {
  resultsExportStudyId: StringParam,
  resultsExportType: StringParam,
  resultsExportStage: StringParam,
  featuresExportScope: StringParam,
  featuresExportExcludedCases: StringParam,
  metadataExportScope: StringParam,
  normalizationExportScope: StringParam,
  polygonsExportScope: StringParam,
  polygonsExportExcludedCases: StringParam,
  workflowExportSource: StringParam,
  workflowExportRun: StringParam,
  workflowExportPanelRuns: StringParam,
};

type ResultsExportQuery = Partial<Record<keyof typeof resultsExportQueryParams, string | null | undefined>>;

const clearedQuery: ResultsExportQuery = {
  resultsExportStudyId: undefined,
  resultsExportType: undefined,
  resultsExportStage: undefined,
  featuresExportScope: undefined,
  featuresExportExcludedCases: undefined,
  metadataExportScope: undefined,
  normalizationExportScope: undefined,
  polygonsExportScope: undefined,
  polygonsExportExcludedCases: undefined,
  workflowExportSource: undefined,
  workflowExportRun: undefined,
  workflowExportPanelRuns: undefined,
};

const draftQueryKeysByType: Partial<Record<ResultsExportType, (keyof ResultsExportQuery)[]>> = {
  features: ['featuresExportScope', 'featuresExportExcludedCases'],
  metadata: ['metadataExportScope'],
  normalization: ['normalizationExportScope'],
  polygons: ['polygonsExportScope', 'polygonsExportExcludedCases'],
};

// Dialog state lives in the URL so it survives a remount or reload; each draft key belongs to one export type.
export const useResultsExportUrlState = () => {
  const [query, setQuery] = useQueryParams(resultsExportQueryParams);

  const update = React.useCallback(
    (changes: ResultsExportQuery, updateType: UrlUpdateType = 'replaceIn') => setQuery(changes, updateType),
    [setQuery]
  );

  const clearDraft = React.useCallback(
    (type: ResultsExportType) => {
      const changes: ResultsExportQuery = {};
      draftQueryKeysByType[type]?.forEach((key) => {
        changes[key] = undefined;
      });
      update(changes);
    },
    [update]
  );

  return { query, update, clearDraft };
};

// One study's export dialog is open at a time; closing clears every export parameter.
export const useResultsExportDialogHost = (studyId?: string) => {
  const { query, update } = useResultsExportUrlState();
  const isOpen = Boolean(studyId) && query.resultsExportStudyId === studyId;

  const open = React.useCallback(
    () => update({ ...clearedQuery, resultsExportStudyId: studyId }, 'pushIn'),
    [update, studyId]
  );
  const close = React.useCallback(() => update(clearedQuery), [update]);

  return { isOpen, open, close };
};

const workflowSources = ['published', 'approved', 'latest', 'run'] as const;
const RUN_SEPARATOR = ',';

// Absent means no run was chosen; an empty value means the user cleared the choice.
export const parseWorkflowExportRunIds = (value?: string | null): string[] | undefined =>
  value === undefined || value === null ? undefined : value.split(RUN_SEPARATOR).filter(Boolean);

// The Workflow export dialogs keep their results source in the URL, apart from the Workflow tab's own mode.
// Specific orchestrations are stored per panel for the open dialog. The old study-wide `workflowExportRun` list is
// not that contract; it is still cleared on close and reported when a link still carries it.
export const useWorkflowExportSourceUrlState = () => {
  const { query, update } = useResultsExportUrlState();
  const source = workflowSources.find((candidate) => candidate === query.workflowExportSource);
  const panelRunSelections = React.useMemo(
    () => parsePanelRunSelections(query.workflowExportPanelRuns),
    [query.workflowExportPanelRuns]
  );
  const ignoredStudyRunIds = React.useMemo(
    () => parseWorkflowExportRunIds(query.workflowExportRun) ?? [],
    [query.workflowExportRun]
  );
  const select = React.useCallback(
    (selection: { source: (typeof workflowSources)[number] }) =>
      update({
        workflowExportSource: selection.source,
        workflowExportRun: undefined,
        workflowExportPanelRuns: undefined,
      }),
    [update]
  );
  const setPanelRunSelections = React.useCallback(
    (selections: Record<string, string[]>) =>
      update({
        workflowExportPanelRuns: encodePanelRunSelections(selections),
        // An explicit per-panel choice replaces a leftover study-wide list.
        ...(Object.keys(selections).length ? { workflowExportRun: undefined } : {}),
      }),
    [update]
  );
  const clear = React.useCallback(
    () => update({ workflowExportSource: undefined, workflowExportRun: undefined, workflowExportPanelRuns: undefined }),
    [update]
  );
  return {
    source,
    panelRunSelections,
    ignoredStudyRunIds,
    select,
    setPanelRunSelections,
    clear,
  };
};
